import { datadogRum } from '@datadog/browser-rum';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect } from 'react';

import { AdSdk, TransferApplicationState } from '@wetransfer/ad-sdk';
import {
  AdtechClient,
  AdtechProduct,
  AdSDKListeners,
} from '@wetransfer/adtech-common';

import {
  FF_ACTIVE_PLACEMENT_OPTIMIZATION,
  FF_WALLPAPER_ROTATION_INTERVAL_PER_COUNTRY,
  EXP_PRO_USER_EDITORIAL_EXCEPTION,
} from '@wetransfer/transfer/constant/experiments';
import { getPlanTier, userHasPaidTier } from '@wetransfer/transfer/helper/user';
import { usePrevious } from '@wetransfer/transfer/hooks/usePrevious';
import { useAdBlockDetection } from '@wetransfer/transfer/modules/ad-block-detection/hooks';
import { getEnv } from '@wetransfer/transfer/modules/ad-sdk-wallpaper/helpers';
import Analytics from '@wetransfer/transfer/modules/analytics/analytics';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import { getFullVariant } from '@wetransfer/transfer/modules/experiments';
import termsAndConditions from '@wetransfer/transfer/modules/terms-and-conditions';
import {
  connector,
  PropsFromRedux,
} from '@wetransfer/transfer/wallpaper-bundle/connector';

import { getGeo, getLanguage } from './helpers/override-helper';
import { openPanel, closePanel } from './helpers/router-helper';
import {
  getNavPosition,
  getTransferBoxPosition,
  showApp,
  hideApp,
  showDropzone,
} from './helpers/ui-helper';
import { getUserKey, hasUserConsentedAdvertisement } from './helpers/user';

export const AdSDKMicroFrontend = ({ state }: PropsFromRedux): JSX.Element => {
  const rootNode = 'renderer-micro-frontend';

  const isAdvertisementEnabled = hasUserConsentedAdvertisement();

  const { isReady: isAdBlockReady, result: adBlockResult } =
    useAdBlockDetection();

  const { auth, session, user, route, layout } = state;

  const language = getLanguage(session, route);
  const geo = getGeo(session, route);
  const key = getUserKey({ auth, session });
  const nav = getNavPosition();
  const transferBox = getTransferBoxPosition();
  const isTermsAccepted = termsAndConditions.areAccepted();
  const userAction = route.path.match(/download/) ? 'download' : 'upload';
  const isPayingUser = userHasPaidTier(user);
  const userPlanTier = getPlanTier(user) ?? null;
  const demoAdvertId = route?.params?.advert_id;

  const activePlacement = getFullVariant(FF_ACTIVE_PLACEMENT_OPTIMIZATION, {
    payload: { maxPosition: 3 },
  });

  const isRTAEnabled = getFullVariant(
    FF_WALLPAPER_ROTATION_INTERVAL_PER_COUNTRY,
    {}
  ).value;

  const isAdsForProEnabled =
    getFullVariant(EXP_PRO_USER_EDITORIAL_EXCEPTION.key, {
      value: EXP_PRO_USER_EDITORIAL_EXCEPTION.default,
    }).value === 'enabled';

  const experiments = {
    activePlacement: {
      maxPosition: activePlacement?.payload?.maxPosition ?? 3,
    },
    reducedRotationIntervals: Boolean(isRTAEnabled),
    adsForPro: isAdsForProEnabled,
  };

  const getInternalState = useCallback(
    () => ({
      isTermsAccepted,
      isAdBlockEnabled: adBlockResult,
      isAdvertisementEnabled,
      client: AdtechClient.Transfer,
      product: AdtechProduct.Web,
      geo,
      language,
      userAgent: Browser.identity,
      user: {
        key,
        isPayingUser,
        id: user?.id,
        auth0UserId: auth?.auth0UserId,
        planTier: userPlanTier,
      },
      nav,
      transferBox,
      isPanelOpen: route.isPanelOpen,
      isPanelHalfOpen: route.isPanelHalfOpen,
      isPanelFullOpen: route.isPanelFullOpen,
      dropzoneActive: layout.dropzoneActive,
      demoAdvertId,
      userAction,
      acceptedLanguages: (
        window['__session__'] as unknown as {
          user: {
            accepted_languages: string[];
          };
        }
      )?.user?.accepted_languages,
    }),
    [
      isTermsAccepted,
      adBlockResult,
      isAdvertisementEnabled,
      geo,
      language,
      key,
      isPayingUser,
      user?.id,
      auth?.auth0UserId,
      nav,
      transferBox,
      route.isPanelOpen,
      route.isPanelHalfOpen,
      route.isPanelFullOpen,
      layout.dropzoneActive,
      userAction,
      demoAdvertId,
      userPlanTier,
    ]
  );

  const prevState = usePrevious(getInternalState());

  useEffect(() => {
    const handleUpdates = () => {
      if (prevState && !isEqual(prevState, getInternalState())) {
        try {
          window.dispatchEvent(
            new CustomEvent<Partial<TransferApplicationState>>(
              AdSDKListeners.AD_SDK_INTERNAL_STATE_UPDATE,
              {
                detail: getInternalState(),
              }
            )
          );
        } catch (error) {
          datadogRum.addError(
            new Error('ad_sdk_internal_state_update_failed'),
            { errorMessage: error.message }
          );
        }
      }
    };

    handleUpdates();
  }, [getInternalState, prevState]);

  useEffect(() => {
    if (!isAdBlockReady) {
      return;
    }

    const startAdSDK = async () => {
      await AdSdk.init({
        adUnitId: rootNode,
        adUnitProduct: AdtechProduct.Web,
        adUnitClient: AdtechClient.Transfer,
        clientEnvironment: getEnv(),
        applicationState: getInternalState(),
        clientEventTracking: {
          trackUnstructSnowplowEvent: (schema: string, event: unknown) => {
            Analytics.trackUnstructSnowplowEvent(schema, event);
          },
        },
        errorTracking: {
          trackError: datadogRum.addError,
          addTiming: datadogRum.addTiming,
          addAction: datadogRum.addAction,
          addRumGlobalContext: datadogRum.addRumGlobalContext,
        },
        clientActions: {
          showDropzone,
          showApp,
          hideApp,
          closePanel,
          navigateTo: openPanel,
        },
        experiments,
      });
    };

    startAdSDK();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdBlockReady]);

  return (
    <div
      className="micro-frontend-container"
      id={rootNode}
      data-testid={rootNode}
    />
  );
};

export default connector(AdSDKMicroFrontend);
